import {hasPermission, isAdministrator} from '../tfi/helpers'
import {ALL_PERMISSIONS, STORAGE_NAMES} from '../tfi/helpers/consts'

type RouteContext = {
  permissions?: string[]
  userGroups?: string[]
}

const getNormalizedPath = (path: string) => path.split('?')[0].split('#')[0]

const isOldDashboardPath = (path: string) =>
  path === '/dashboard' || path === '/dashboard/lead'

const getStoredLastRoute = () => {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(STORAGE_NAMES.LAST_ROUTE)
}

const getPermissionsContext = ({permissions = [], userGroups = []}: RouteContext) => {
  const isAgente = userGroups.includes('Agente')
  const isSupervisor = userGroups.includes('Supervisor')
  const isAdmin = isAdministrator(permissions)

  const canViewPolicyDashboard =
    permissions.length > 0 &&
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY) &&
    (isAdmin || isAgente || isSupervisor)

  return {
    canViewPolicyDashboard,
    canViewLeads: permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_LEAD),
    canViewTasks: permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_TASKS),
    canViewUsers: permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_USER),
    canViewPolicy: permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY),
    canViewSignatureForms:
      permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS),
    canViewContacts:
      permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT),
    canViewAttentionTickets:
      permissions.length > 0 &&
      hasPermission(permissions, ALL_PERMISSIONS.VIEW_TICKET),
    canAddTickets:
      permissions.length > 0 &&
      hasPermission(permissions, ALL_PERMISSIONS.ADD_TICKET),
    canViewCommissions:
      permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMISSION),
    canViewResourceCenter:
      permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_RESOURCE_CENTER),
    canViewIntegrations:
      permissions.length > 0 && hasPermission(permissions, ALL_PERMISSIONS.VIEW_INTEGRATION),
    canViewNotifications: true,
  }
}

export const canAccessPolicyDashboard = (context: RouteContext) =>
  getPermissionsContext(context).canViewPolicyDashboard

const getFirstPermittedRoute = (ctx: ReturnType<typeof getPermissionsContext>) => {
  const candidates = [
    {path: '/dashboard/policy', can: ctx.canViewPolicyDashboard},
    {path: '/leads', can: ctx.canViewLeads},
    {path: '/kanban', can: ctx.canViewTasks},
    {path: '/users', can: ctx.canViewUsers},
    {path: '/policies', can: ctx.canViewPolicy},
    {path: '/quotes', can: ctx.canViewPolicy},
    {path: '/signature-forms', can: ctx.canViewSignatureForms},
    {path: '/contacts', can: ctx.canViewContacts},
    {path: '/tickets', can: ctx.canViewAttentionTickets},
    {path: '/tickets/new', can: ctx.canAddTickets},
    {path: '/commissions', can: ctx.canViewCommissions},
    {path: '/resource-center', can: ctx.canViewResourceCenter},
    {path: '/notifications', can: ctx.canViewNotifications},
    {path: '/integrations', can: ctx.canViewIntegrations},
    {path: '/calendar', can: true},
  ]

  return candidates.find((candidate) => candidate.can)?.path
}

const isAllowedRoute = (path: string, ctx: ReturnType<typeof getPermissionsContext>) => {
  const normalizedPath = getNormalizedPath(path)

  if (isOldDashboardPath(normalizedPath)) return false

  const matchers = [
    {can: ctx.canViewPolicyDashboard, match: (p: string) => p === '/dashboard/policy'},
    {can: ctx.canViewLeads, match: (p: string) => p === '/leads' || p.startsWith('/lead/')},
    {can: ctx.canViewTasks, match: (p: string) => p === '/kanban'},
    {can: ctx.canViewUsers, match: (p: string) => p === '/users' || p.startsWith('/user/')},
    {
      can: ctx.canViewPolicy,
      match: (p: string) => p === '/policies' || p.startsWith('/one-policy/'),
    },
    {
      can: ctx.canViewPolicy,
      match: (p: string) =>
        p === '/quotes' || p.startsWith('/quote/') || p.startsWith('/one-quote/'),
    },
    {
      can: ctx.canViewSignatureForms,
      match: (p: string) => p === '/signature-forms' || p.startsWith('/signature-forms/'),
    },
    {can: ctx.canViewContacts, match: (p: string) => p === '/contacts'},
    {
      can: ctx.canViewAttentionTickets,
      match: (p: string) =>
        p === '/tickets' ||
        (p.startsWith('/tickets/') && p !== '/tickets/new') ||
        p === '/attention-tickets' ||
        p === '/tickets-atencion',
    },
    {
      can: ctx.canAddTickets,
      match: (p: string) => p === '/tickets/new',
    },
    {
      can: ctx.canViewCommissions,
      match: (p: string) => p === '/commissions' || p.startsWith('/commissions/'),
    },
    {can: ctx.canViewResourceCenter, match: (p: string) => p === '/resource-center'},
    {can: ctx.canViewNotifications, match: (p: string) => p === '/notifications'},
    {can: ctx.canViewIntegrations, match: (p: string) => p === '/integrations'},
    {can: true, match: (p: string) => p === '/calendar'},
  ]

  return matchers.some(({can, match}) => can && match(normalizedPath))
}

export const getLandingRoute = (context: RouteContext) => {
  const ctx = getPermissionsContext(context)
  const lastRoute = getStoredLastRoute()
  const lastRouteAllowed = lastRoute ? isAllowedRoute(lastRoute, ctx) : false

  if (ctx.canViewPolicyDashboard) {
    return '/dashboard/policy'
  }

  if (lastRoute && lastRouteAllowed) {
    return lastRoute
  }

  return getFirstPermittedRoute(ctx) ?? '/error/404'
}
